import { z } from 'zod';

import { ApiError } from '@/lib/api';
import { errorAdvice } from '@/lib/error-advice';

// Acceso por tipo de documento (spec 2026-10-14 §8 D9): contrato con Zod (ENG-VAL-01),
// etiquetas en palabras (UX-COPY-01), la coherencia lectura/acción antes de guardar y
// los rechazos del servidor en palabras (UX-ERR-01).

export const CAPABILITIES = ['read', 'edit_online', 'print', 'comment'] as const;
export type Capability = (typeof CAPABILITIES)[number];

export const CAPABILITY_LABELS: Readonly<Record<Capability, string>> = {
  read: 'Consultar',
  edit_online: 'Editar en línea',
  print: 'Imprimir y descargar',
  comment: 'Comentar',
};

const rulesSchema = z.object({
  read: z.array(z.string()),
  edit_online: z.array(z.string()),
  print: z.array(z.string()),
  comment: z.array(z.string()),
});
export type Rules = z.infer<typeof rulesSchema>;

const roleSchema = z.object({ id: z.string(), code: z.string(), name: z.string() });
export type AccessRole = z.infer<typeof roleSchema>;

export const typeAccessSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  rules: rulesSchema,
});
export type TypeAccess = z.infer<typeof typeAccessSchema>;

export const documentAccessResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ roles: z.array(roleSchema), types: z.array(typeAccessSchema) }),
});
export type DocumentAccess = z.infer<typeof documentAccessResponseSchema>['data'];

export const typeAccessResponseSchema = z.object({
  success: z.literal(true),
  data: typeAccessSchema,
});

export function emptyRules(): Rules {
  return { read: [], edit_online: [], print: [], comment: [] };
}

/** Lo que muestra una celda: "Según el rol" sin regla, o los roles de la regla. */
export function ruleSummary(
  roleIds: readonly string[],
  roles: ReadonlyMap<string, AccessRole>,
): string {
  if (roleIds.length === 0) {
    return 'Según el rol';
  }
  return roleIds.map((id) => roles.get(id)?.name ?? 'Rol retirado').join(', ');
}

function cannotReadText(roleName: string, capability: Capability): string {
  return `${roleName} puede ${CAPABILITY_LABELS[capability].toLowerCase()}, pero no consultar este tipo. Márcalo también en Consultar.`;
}

/** Coherencia antes de guardar: con regla de lectura, quien edita, imprime o comenta
 *  debe estar en ella. Sin regla de lectura rige la lectura del rol (la valida el servidor). */
export function localRuleError(
  rules: Rules,
  roles: ReadonlyMap<string, AccessRole>,
): string | null {
  if (rules.read.length === 0) {
    return null;
  }
  for (const capability of ['edit_online', 'print', 'comment'] as const) {
    const missing = rules[capability].find((id) => !rules.read.includes(id));
    if (missing !== undefined) {
      return cannotReadText(roles.get(missing)?.name ?? 'Un rol', capability);
    }
  }
  return null;
}

function isCapability(value: unknown): value is Capability {
  return typeof value === 'string' && (CAPABILITIES as readonly string[]).includes(value);
}

/** Rechazo del guardado en palabras: qué pasó y qué hacer. */
export function accessErrorText(err: unknown, roles: ReadonlyMap<string, AccessRole>): string {
  if (!(err instanceof ApiError)) {
    return `No se pudo guardar. ${errorAdvice(err)}`;
  }
  const meta = err.meta ?? {};
  if (meta.reason === 'role_cannot_read' && isCapability(meta.capability)) {
    const name = typeof meta.roleId === 'string' ? roles.get(meta.roleId)?.name : undefined;
    return cannotReadText(name ?? 'Un rol', meta.capability);
  }
  if (meta.reason === 'role_not_eligible') {
    return 'Uno de los roles ya no puede tener reglas por tipo. Recarga la página e intenta de nuevo.';
  }
  switch (err.status) {
    case 403:
      return 'No tienes permiso para administrar el acceso por tipo de documento.';
    case 404:
      return 'Este tipo de documento ya no existe en la empresa activa. Recarga la página.';
    case 400:
      return 'Los datos no son válidos. Recarga la página e intenta de nuevo.';
    default:
      return 'No se pudo guardar. Intenta de nuevo y, si persiste, contacta a soporte.';
  }
}
